import React from "react";

import NotificationSettingItem from "./notificationsetting";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { routes } from "utilities/routing/generatedRoutes";

interface IProps {
  initialSettings: server.dto.NotificationSettings
}

interface IState {
  settings: server.dto.NotificationTypePreference[]
  submitting: boolean
}

const genericSettings: server.dto.NotificationTypes[] = ["taskDue", "bookingRequest"];
const advancingSettings: server.dto.NotificationTypes[] = ["advancingFileAdded", "advancingDataDeclined", "advancingMessageSent", "advancingNewData"];
const showSettings: server.dto.NotificationTypes[] = ["showStatusChanged"];

class NotificationSettingsComponent extends React.Component<IProps, IState> {
  state: IState = {
    settings: this.props.initialSettings.settings,
    submitting: false
  };

  areAllBrowserSettingsEnabled = (): boolean => this
    .state
    .settings
    .every(s => s.browser);

  areAllMobileSettingsEnabled = (): boolean => this.state.settings.find(s => s.type === "showStatusChanged")?.mobile ?? false;

  onBrowserSelected = () => {
    const shouldEnableAll = !this.areAllBrowserSettingsEnabled();

    const newSettings = this.state.settings.map(setting => ({
      ...setting,
      browser: shouldEnableAll
    }));

    this.setState({ settings: newSettings });
  };

  onMobileSelected = () => {
    this.onMobileChange("showStatusChanged");
  };

  onBrowserChange = (type: server.dto.NotificationTypes) => {
    const settingIdx = this.state.settings.findIndex(setting => setting.type === type);
    const oldSetting = this.state.settings[settingIdx];
    const newSetting: server.dto.NotificationTypePreference = {
      ...oldSetting,
      browser: !oldSetting.browser
    };

    const newSettings = [...this.state.settings];
    newSettings[settingIdx] = newSetting;

    this.setState({ settings: newSettings });
  };

  onMobileChange = (type: server.dto.NotificationTypes) => {
    const settingIdx = this.state.settings.findIndex(setting => setting.type === type);
    const oldSetting = this.state.settings[settingIdx];
    const newSetting: server.dto.NotificationTypePreference = {
      ...oldSetting,
      mobile: !oldSetting.mobile
    };

    const newSettings = [...this.state.settings];
    newSettings[settingIdx] = newSetting;

    this.setState({ settings: newSettings });
  };

  onSave = () => {
    const data: server.dto.NotificationSettings = {
      settings: this.state.settings
    };

    this.setState({ submitting: true });
    window.CoreApi.Notification.UpdateSettings(data)
      .then(() => { window.location.href = routes.personal_SettingMenu().absolute });
  };

  hasGenericSettings = (): boolean => this.props.initialSettings.settings.some(setting => genericSettings.includes(setting.type));

  hasAdvancingSettings = (): boolean => this.props.initialSettings.settings.some(setting => advancingSettings.includes(setting.type));

  hasShowSettings = (): boolean => this.props.initialSettings.settings.some(setting => showSettings.includes(setting.type));

  render(): JSX.Element {
    const settingsMap: { [key in server.dto.NotificationTypes]: server.dto.NotificationTypePreference } = this.state.settings
      .reduce<any>((acc, curr) => ({
        ...acc,
        [curr.type]: curr
      }), {});

    const hasSetting = (setting: server.dto.NotificationTypes): boolean => settingsMap[setting] != null;

    return (
      <>
        <div className="notificationsettingscontent content-section">
          <div className="topic">
            <div className="flex gap-large">
              <div className="grow1"></div>
              <div className="checkboxcell">
                <Checkbox
                  checked={this.areAllBrowserSettingsEnabled()}
                  onChange={this.onBrowserSelected}
                  label={RESX.GeneralLabels.Browser}
                />
              </div>
              <div className="checkboxcell">
                <Checkbox
                  checked={this.areAllMobileSettingsEnabled()}
                  onChange={this.onMobileSelected}
                  label={RESX.NotificationSettings.Mobile}
                />
              </div>
            </div>

          </div>

          {this.hasGenericSettings() &&
            <div className="topic">
              <div className="header">
                <div>
                  {RESX.NotificationSettings.NotifyMeWhen}
                </div>
              </div>
              <Rows>
                {hasSetting("taskDue") &&
                  <NotificationSettingItem
                    label={RESX.NotificationSettings.MyTasksAreDue}
                    value="taskDue"
                    browserChecked={settingsMap.taskDue.browser}
                    onBrowserChange={this.onBrowserChange} />}

                {hasSetting("bookingRequest") &&
                  <NotificationSettingItem
                    label={RESX.NotificationSettings.ANewBookingRequestIsReceived}
                    value="bookingRequest"
                    browserChecked={settingsMap.bookingRequest.browser}
                    onBrowserChange={this.onBrowserChange} />}
              </Rows>
            </div>}

          {this.hasShowSettings() &&
            <div className="topic">
              <div className="header">
                <div>
                  {RESX.NotificationSettings.NotifyMeAbout} <strong>{RESX.Show.Shows.toLowerCase()
                  }</strong> {RESX.NotificationSettings.When}
                </div>
              </div>
              <Rows>
                {hasSetting("showStatusChanged") &&
                  <NotificationSettingItem
                    label={RESX.NotificationSettings.TheStatusIsChanged}
                    value="showStatusChanged"
                    mobileChecked={settingsMap.showStatusChanged.mobile}
                    browserChecked={settingsMap.showStatusChanged.browser}
                    onMobileChange={this.onMobileChange}
                    onBrowserChange={this.onBrowserChange} />}
              </Rows>
            </div>
          }
          {this.hasAdvancingSettings() &&
            <div className="topic">
              <div className="header">
                <div>
                  {RESX.NotificationSettings.NotifyMeAbout} <strong>{RESX.Advancing.resxAdvancing
                    .toLowerCase()}</strong> {RESX.NotificationSettings.When}
                </div>
              </div>

              <Rows>
                {hasSetting("advancingFileAdded") &&
                  <NotificationSettingItem
                    label={RESX.NotificationSettings.AFileIsAdded}
                    value="advancingFileAdded"
                    browserChecked={settingsMap.advancingFileAdded.browser}
                    onBrowserChange={this.onBrowserChange} />
                }
                {hasSetting("advancingMessageSent") &&
                  <NotificationSettingItem
                    label={RESX.NotificationSettings.AMessageIsSent}
                    value="advancingMessageSent"
                    browserChecked={settingsMap.advancingMessageSent.browser}
                    onBrowserChange={this.onBrowserChange} />
                }
                {hasSetting("advancingNewData") &&
                  <NotificationSettingItem
                    label={RESX.NotificationSettings.NewInformationIsProvided}
                    value="advancingNewData"
                    browserChecked={settingsMap.advancingNewData.browser}
                    onBrowserChange={this.onBrowserChange} />
                }
                {hasSetting("advancingDataDeclined") &&
                  <NotificationSettingItem
                    label={RESX.NotificationSettings.InputIsDeclined}
                    value="advancingDataDeclined"
                    browserChecked={settingsMap.advancingDataDeclined.browser}
                    onBrowserChange={this.onBrowserChange} />
                }
              </Rows>
            </div>
          }
        </div>

        <div className="flex margin-top-bigger justify-end">
          <div>
            <button
              type="button"
              disabled={this.state.submitting}
              onClick={this.onSave}
              className="button-new primary">
              {RESX.Buttons.btnSave}
            </button>
          </div>
        </div>
      </>
    );
  }
}

const Rows = (props: React.PropsWithChildren<unknown>) => {
  return <div className="flex-column gap-small">
    {props.children}
  </div>
}

export default NotificationSettingsComponent;
